import {
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { format } from 'date-fns';
import React from 'react';
import { Line } from 'react-chartjs-2';
import IRRDataPoint from '../models/IRRDataPoint';
import ExtendedLiquid from '../services/ExtendedLiquid';
import { Palette } from 'Palette';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Filler);

interface IRRLineChartProps {
  data: IRRDataPoint[];
  tone: keyof typeof Palette;
  /** Admin's Fund Management preview uses the app's actual primary button colour instead of the dedicated graph-line colour. */
  canEdit?: boolean;
}

export default function IRRLineChart({ data, tone, canEdit = false }: IRRLineChartProps) {
  const labels = data.map((d) => format(new Date(`${d.month}-01`), 'MMM yy'));
  const values = data.map((d) => d.cumulative_value!);
  const types: ('actual' | 'forecast')[] = data.map((d) => d.type);
  const lineColor = canEdit ? Palette[tone].buttonBg! : Palette[tone].graphLine!;

  const chartData: ChartData<'line'> = {
    labels,
    datasets: [
      {
        label: 'Cumulative',
        data: values,
        fill: false,
        borderWidth: 2,
        borderColor: lineColor, // Default color
        backgroundColor: lineColor,
        pointRadius: 0,
        tension: 0,
        borderCapStyle: 'round',
        segment: {
          borderDash: (ctx) => {
            const index = ctx.p0DataIndex; // Current segment start index
            const type = types[index]; // Get the type ("actual" or "forecast")
            return type === 'forecast' ? [1, 6] : undefined; // Dotted for "forecast", solid for "actual"
          },
        },
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: import('chart.js').TooltipItem<'line'>) => {
            const value = ExtendedLiquid.currencyMillions(
              context.raw as number,
              true,
              true,
              true,
              true,
            );
            const type = types[context.dataIndex];
            return `${type === 'forecast' ? 'Forecast' : 'Actual'}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: string | number) => {
            return ExtendedLiquid.currencyMillions(value as number, true, true, true);
          },
        },
      },
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
  };

  return (
    <>
      <div style={{ width: '100%', height: 500 }} className="react-IRRLineChart">
        <Line data={chartData} options={options} />
      </div>
    </>
  );
}
